import { defineStore } from 'pinia';
import type { User } from 'firebase/auth';
import ClientAppError from '../modules/utils/clientAppError';
import { handleError } from '@modules/utils/errorHandler';
import { auth } from '@modules/firebase/firebaseInit';
import { loginWithEmailAndPassword } from '@modules/authService';

type UserRole = 'MIRENCO_ADMIN' | 'USER' | null;

// Storage service abstraction for better testability
export const storageService = {
  setItem: (key: string, value: string) => window.localStorage.setItem(key, value),
  removeItem: (key: string) => window.localStorage.removeItem(key),
};

type UserState = {
  isLoggedIn: boolean;
  userName: string | null;
  userEmail: string | null;
  userRole: UserRole;
  loading: boolean;
}

 
export const useUserStore = defineStore('user', {
  state: ():UserState => ({
    isLoggedIn: false,
    userName: null,
    userEmail: null,
    userRole: null,
    loading: false,
  }),
  actions: {
    async login(
      email: string, 
      password: string,
      authService = loginWithEmailAndPassword,
      storage = storageService
    ): Promise<User | null> {
      this.loading = true;
      try {
        const result = await authService(email, password);

        if (result.error || !result.user) {
          if (result.error) {
            handleError(result.error, 'userStore.login');
            // Throw the error so it can be caught by the component
            throw result.error;
          }
          throw new Error('Login failed');
        }

        const { user, userRole } = result;

        this.isLoggedIn = true;
        this.userEmail = user.email;
        this.userName = user.displayName;
        this.userRole = userRole as UserRole;

        storage.setItem('user', JSON.stringify(user));
        return user;
      } finally {
        this.loading = false;
      }
    },
    async logout(authInstance = auth, storage = storageService): Promise<void> {
      this.loading = true;
      try {
        await authInstance.signOut();
        this.resetState();
        storage.removeItem('user');
      } catch (error) {
        handleError(
          error,
          'userStore.logout',
        );
      } finally {
        this.loading = false;
      }
    },
    async checkAuthState(authInstance = auth, storage = storageService): Promise<User | null> {
      this.loading = true;

      try {
        const user = await new Promise<User|null>((resolve) => {
          const unsubscribe = authInstance.onAuthStateChanged((authUser) => {
            unsubscribe();
            resolve(authUser);
          });
        });

        if (!user) {
          this.resetState();
          storage.removeItem('user');
          return null;
        }

        try {
          const idTokenResult = await user.getIdTokenResult();
          const { role } = idTokenResult.claims;

          if (!role) {
            handleError(
              ClientAppError.authError('Role claim not found in user token'),
              'userStore.checkAuthState.roleValidation',
            );
            // Don't call logout here - just reset state and sign out
            await authInstance.signOut();
            this.resetState();
            storage.removeItem('user');
            return null;
          }

          // Only set state if role validation passes
          this.isLoggedIn = true;
          this.userEmail = user.email;
          this.userName = user.displayName;
          this.userRole = String(role) as UserRole;
          storage.setItem('user', JSON.stringify(user));
          return user;
        } catch (error) {
          handleError(error, 'userStore.checkAuthState');
          // Don't call logout here - just reset state and sign out
          await authInstance.signOut();
          this.resetState();
          storage.removeItem('user');
          return null;
        }
      } catch (error) {
        handleError(
          error,
          'userStore.checkAuthState',
        );
        this.resetState();
        storage.removeItem('user');
        return null;
      } finally {
        this.loading = false;
      }
    },
    resetState(): void {
      this.isLoggedIn = false;
      this.userEmail = null;
      this.userName = null;
      this.userRole = null;
    },

  },
  getters: {
    getUserEmail():string {
      if (this.userEmail === null) throw ClientAppError.authError('User email is null');
      return this.userEmail;
    },
    getUserName():string {
      if (this.userName === null) throw ClientAppError.authError('User name is null');
      return this.userName;
    },
    isAdmin():boolean {
      if (this.userRole === null) throw ClientAppError.authError('User role is null');
      return this.userRole === 'MIRENCO_ADMIN';
    },
  },
});
